import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Aviso, Campo, Modal } from "./piezas";
import AvisoSuscripcion from "./AvisoSuscripcion";
import EstadoConexion from "./EstadoConexion";
import BloqueoInactividad from "./BloqueoInactividad";
import { useSesion, type Permiso } from "../lib/sesion";
import { SelectorIdioma, useIdioma } from "../lib/idioma";
import { es, type Clave } from "../idiomas/es";
import { iniciales } from "../lib/formato";

interface Entrada {
  a: string;
  clave: Clave;
  icono: string;
  /** Uno, o varios: la entrada se ve si la cuenta tiene alguno. */
  permiso?: Permiso | Permiso[];
  /** Otras direcciones que son la misma pantalla y encienden esta entrada. */
  tambien?: string[];
  grupo: string;
}

/*
 * El nombre de cada grupo, en el diccionario. El grupo en si sigue siendo la
 * palabra en castellano porque es lo que se guarda en el navegador para
 * recordar cual esta plegado: traducirlo desplegaria los grupos al cambiar de
 * idioma, y quien se lo encontrara pensaria que se ha roto algo.
 */
const NOMBRE_DEL_GRUPO: Record<string, Clave> = {
  Asociación: "nav.grupoAsociacion",
  Ajustes: "nav.grupoAjustes",
};

/*
 * El menú, en dos grupos.
 *
 * Arriba, sueltos, los dos que se abren a cada rato. Debajo, «Asociación» con
 * el trabajo del local y «Ajustes» con lo demás; el orden dentro de cada uno es
 * el que pidió el club, no el alfabético ni el del código.
 *
 * Los grupos se pliegan desde su cabecera: con veintiún enlaces y el zoom del
 * navegador subido —lo normal en el ordenador del mostrador— el menú no cabe
 * y había que desplazarlo para llegar abajo.
 */
const MENU: Entrada[] = [
  { a: "/", clave: "nav.inicio", icono: "◆", grupo: "" },
  {
    a: "/mostrador",
    clave: "nav.mostrador",
    icono: "▤",
    grupo: "",
    permiso: "dispensar",
  },

  {
    a: "/donaciones",
    clave: "nav.donaciones",
    icono: "♥",
    grupo: "Asociación",
    permiso: "gestionarGastos",
  },
  {
    a: "/socios",
    clave: "nav.socios",
    icono: "☺",
    grupo: "Asociación",
    permiso: "verSocios",
  },
  {
    a: "/caja",
    clave: "nav.caja",
    icono: "▣",
    grupo: "Asociación",
    permiso: "gestionarCaja",
  },
  /*
   * Gastos y compras son una sola entrada: las dos son dinero que sale, y
   * tenerlas en sitios distintos obligaba a acordarse de en cual de los dos
   * estaba lo que se busca. Dentro se cambia de pestaña.
   */
  {
    a: "/gastos",
    clave: "nav.gastos",
    icono: "↓",
    grupo: "Asociación",
    permiso: ["gestionarGastos", "gestionarInventario"],
    tambien: ["/compras"],
  },
  {
    a: "/inventario",
    clave: "nav.inventario",
    icono: "▦",
    grupo: "Asociación",
    permiso: "gestionarInventario",
  },
  {
    a: "/recuentos",
    clave: "nav.recuentos",
    icono: "⚖",
    grupo: "Asociación",
    permiso: "gestionarInventario",
  },
  {
    a: "/barra",
    clave: "nav.barra",
    icono: "☕",
    grupo: "Asociación",
    permiso: "venderBarra",
  },
  {
    a: "/dispensaciones",
    clave: "nav.dispensaciones",
    icono: "≡",
    grupo: "Asociación",
    permiso: "dispensar",
  },
  {
    a: "/descuentos",
    clave: "nav.descuentos",
    icono: "%",
    grupo: "Asociación",
    permiso: "verCatalogo",
  },

  {
    a: "/informes",
    clave: "nav.informes",
    icono: "◔",
    grupo: "Ajustes",
    permiso: "verInformes",
  },
  {
    a: "/ajustes",
    clave: "nav.ajustes",
    icono: "⚙",
    grupo: "Ajustes",
    permiso: "ajustesClub",
  },
  {
    a: "/dias",
    clave: "nav.dias",
    icono: "▧",
    grupo: "Ajustes",
    permiso: "verInformes",
  },
  {
    a: "/socios/contrato",
    clave: "nav.contrato",
    icono: "✎",
    grupo: "Ajustes",
    permiso: "verSocios",
  },
  {
    a: "/accesos",
    clave: "nav.accesos",
    icono: "⇥",
    grupo: "Ajustes",
    permiso: "registrarAcceso",
  },
  {
    a: "/renovaciones",
    clave: "nav.renovaciones",
    icono: "↻",
    grupo: "Ajustes",
    permiso: "cobrarCuota",
  },
  {
    a: "/cuotas",
    clave: "nav.cuotas",
    icono: "◈",
    grupo: "Ajustes",
    permiso: "verSocios",
  },
  {
    a: "/comunidad",
    clave: "nav.comunidad",
    icono: "✉",
    grupo: "Ajustes",
    permiso: "gestionarSolicitudes",
  },
  {
    a: "/catalogo",
    clave: "nav.catalogo",
    icono: "❁",
    grupo: "Ajustes",
    permiso: "verCatalogo",
  },
  {
    a: "/cartas",
    clave: "nav.cartas",
    icono: "▥",
    grupo: "Ajustes",
    permiso: "ajustesClub",
  },
];

/*
 * Los grupos plegados se recuerdan en este navegador, no en la cuenta: la
 * tablet del mostrador y el ordenador del despacho no tienen por qué verlo
 * igual. Un grupo plegado sigue enseñando la pantalla abierta si es de las
 * suyas (ver `.nav-plegado` en estilos.css), así que plegar «Ajustes» estando
 * en Informes no deja a nadie sin saber dónde está.
 */
const CLAVE_PLEGADOS = "clubs.menu.plegados";

function leerPlegados(): string[] {
  try {
    const guardado: unknown = JSON.parse(
      localStorage.getItem(CLAVE_PLEGADOS) ?? "[]",
    );
    return Array.isArray(guardado)
      ? guardado.filter((g): g is string => typeof g === "string")
      : [];
  } catch {
    // Un valor roto o un navegador sin almacenamiento: se abre todo.
    return [];
  }
}

/**
 * Cada cuanto la pantalla pregunta si su sesion sigue valiendo.
 *
 * Veinte segundos es lo que tarda en apagarse una pantalla despues de que el
 * propietario cierre las sesiones. Bajarlo mas no gana nada: quien entra por
 * la puerta no llega al mostrador en menos.
 */
const CADA_CUANTO_LATE_MS = 20_000;

/**
 * Pregunta cada poco si la sesion sigue en pie.
 *
 * No hace falta mirar la respuesta: lo unico que importa es el 401, que `api()`
 * ya convierte en cerrar la sesion y volver a la pantalla de entrada. Sin este
 * latido, una pantalla que no pide nada no se entera nunca de que la han echado
 * y se queda con el listado de socios puesto.
 *
 * Con la pestaña tapada no late: ahi no hay nada que ver, y en cuanto vuelve a
 * primer plano pregunta al momento, que es cuando alguien la esta mirando.
 */
function LatidoDeSesion() {
  useEffect(() => {
    const latir = () => {
      if (document.hidden) return;
      void api("/auth/yo").catch(() => undefined);
    };
    const reloj = setInterval(latir, CADA_CUANTO_LATE_MS);
    document.addEventListener("visibilitychange", latir);
    return () => {
      clearInterval(reloj);
      document.removeEventListener("visibilitychange", latir);
    };
  }, []);
  return null;
}

export default function Marco({ children }: { children: ReactNode }) {
  const { t } = useIdioma();
  const { usuario, club, salir, puede } = useSesion();
  const [cambiandoPassword, setCambiandoPassword] = useState(false);
  const navegacion = useRef<HTMLElement>(null);

  const [plegados, setPlegados] = useState<string[]>(leerPlegados);
  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_PLEGADOS, JSON.stringify(plegados));
    } catch {
      // Sin almacenamiento —navegación privada, datos bloqueados— se abre todo la próxima vez.
    }
  }, [plegados]);
  const plegar = (grupo: string) =>
    setPlegados((antes) =>
      antes.includes(grupo)
        ? antes.filter((g) => g !== grupo)
        : [...antes, grupo],
    );

  /*
   * Publica el alto del menú en `--alto-nav`.
   *
   * En tablet y móvil el menú se va abajo, fijo, y lo que quiera ponerse encima
   * —la barra del total en el mostrador— necesita saber cuánto ocupa. Su alto
   * depende del ancho de la pantalla y de cuántos enlaces vea cada rol, así que
   * ponerlo a mano en el CSS acaba tapando cosas en la pantalla de alguien.
   */
  useEffect(() => {
    const nav = navegacion.current;
    if (!nav || typeof ResizeObserver === "undefined") return;

    const medir = () => {
      document.documentElement.style.setProperty(
        "--alto-nav",
        `${Math.round(nav.getBoundingClientRect().height)}px`,
      );
    };
    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(nav);
    return () => observador.disconnect();
  }, []);

  const visibles = MENU.filter(
    (e) => !e.permiso || [e.permiso].flat().some(puede),
  );

  /*
   * A que entrada del menu pertenece la pantalla abierta: la mas concreta que
   * encaja. Marcar todas las que empiezan igual encendia «Socios» y «Contrato»
   * a la vez, y con dos encendidas no se sabe donde esta uno. Ordenar por
   * longitud tambien deja bien las pantallas sin entrada propia: la ficha de un
   * socio enciende «Socios», que es de donde se ha venido.
   */
  const { pathname } = useLocation();
  const encaja = (ruta: string) =>
    ruta === "/"
      ? pathname === "/"
      : pathname === ruta || pathname.startsWith(`${ruta}/`);
  const activa = visibles
    .filter((e) => [e.a, ...(e.tambien ?? [])].some(encaja))
    .sort((a, b) => b.a.length - a.a.length)[0]?.a;
  const grupos = [...new Set(visibles.map((e) => e.grupo))];

  return (
    <div className="app">
      <nav className="lateral" ref={navegacion}>
        <div className="marca">
          <div className="logo-circulo">{club?.nombre.charAt(0) ?? "C"}</div>
          <div>
            <strong>{club?.nombre ?? "Club"}</strong>
            <span>{t("nav.gestionDe")}</span>
          </div>
        </div>

        {grupos.map((grupo) => {
          const plegado = grupo !== "" && plegados.includes(grupo);
          return (
            <div
              key={grupo || "principal"}
              className={plegado ? "nav-plegado" : undefined}
            >
              {grupo && (
                <button
                  type="button"
                  className="nav-grupo"
                  aria-expanded={!plegado}
                  onClick={() => plegar(grupo)}
                >
                  {t(NOMBRE_DEL_GRUPO[grupo] ?? "nav.grupoAjustes")}
                  <span aria-hidden="true">{plegado ? "▸" : "▾"}</span>
                </button>
              )}
              {visibles
                .filter((e) => e.grupo === grupo)
                .map((e) => (
                  <NavLink
                    key={e.a}
                    to={e.a}
                    className={`nav-enlace${e.a === activa ? " activo" : ""}`}
                  >
                    <span className="icono">{e.icono}</span>
                    {t(e.clave)}
                  </NavLink>
                ))}
            </div>
          );
        })}

        <div className="pie">
          <div className="usuario-chip">
            <div className="avatar">{iniciales(usuario?.nombre ?? "?")}</div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {usuario?.nombre}
              </div>
              <div style={{ fontSize: "0.74rem", color: "#8fa89b" }}>
                {nombreDelRol(t, usuario?.rol)}
              </div>
            </div>
          </div>
          <button
            className="nav-enlace"
            style={{
              width: "100%",
              cursor: "pointer",
              border: 0,
              background: "none",
            }}
            onClick={() => setCambiandoPassword(true)}
          >
            <span className="icono">✱</span> {t("nav.contrasena")}
          </button>
          <button
            className="nav-enlace"
            style={{
              width: "100%",
              cursor: "pointer",
              border: 0,
              background: "none",
            }}
            onClick={() => void salir()}
          >
            <span className="icono">⏻</span> {t("general.salir")}
          </button>
          {/* El idioma, abajo del todo: se toca una vez y no se vuelve. */}
          <div className="nav-idioma">
            <SelectorIdioma compacto />
          </div>
        </div>
      </nav>

      <main className="contenido">
        <EstadoConexion />
        <AvisoSuscripcion />
        {children}
      </main>

      {cambiandoPassword && (
        <DialogoPassword alCerrar={() => setCambiandoPassword(false)} />
      )}

      <LatidoDeSesion />

      <BloqueoInactividad
        nombre={usuario?.nombre ?? ""}
        subtitulo={nombreDelRol(t, usuario?.rol)}
        comprobar={(password) =>
          api("/auth/comprobar-password", {
            metodo: "POST",
            cuerpo: { password },
          })
        }
        alSalir={() => void salir()}
      />
    </div>
  );
}

/** «Propietario» / «Propietari»; si el rol es nuevo, se enseña tal cual. */
function nombreDelRol(t: (c: Clave) => string, rol: string | undefined) {
  const clave = `rol.${rol}` as Clave;
  return clave in es ? t(clave) : rol;
}

function DialogoPassword({ alCerrar }: { alCerrar: () => void }) {
  const { t } = useIdioma();
  const [actual, setActual] = useState("");
  const [nueva, setNueva] = useState("");
  const [repetir, setRepetir] = useState("");
  const [error, setError] = useState("");
  const [hecho, setHecho] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const valida = actual.length > 0 && nueva.length >= 8 && nueva === repetir;

  return (
    <Modal
      titulo={t("nav.pw.titulo")}
      alCerrar={alCerrar}
      acciones={
        hecho ? (
          <button className="boton" onClick={alCerrar}>
            {t("g.cerrar")}
          </button>
        ) : (
          <>
            <button className="boton secundario" onClick={alCerrar}>
              {t("g.cancelar")}
            </button>
            <button
              className="boton"
              disabled={enviando || !valida}
              onClick={async () => {
                setEnviando(true);
                setError("");
                try {
                  await api("/auth/cambiar-password", {
                    metodo: "POST",
                    cuerpo: { actual, nueva },
                  });
                  setHecho(true);
                } catch (fallo) {
                  setError(
                    fallo instanceof Error
                      ? fallo.message
                      : t("nav.pw.fallo"),
                  );
                } finally {
                  setEnviando(false);
                }
              }}
            >
              {enviando ? t("g.guardando") : t("nav.pw.cambiar")}
            </button>
          </>
        )
      }
    >
      {hecho ? (
        <Aviso tipo="ok">{t("nav.pw.hecho")}</Aviso>
      ) : (
        <>
          <Aviso tipo="error">{error}</Aviso>
          <Campo etiqueta={t("nav.pw.actual")}>
            <input
              type="password"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          </Campo>
          <Campo etiqueta={t("nav.pw.nueva")} ayuda={t("nav.pw.minimo")}>
            <input
              type="password"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
              autoComplete="new-password"
            />
          </Campo>
          <Campo etiqueta={t("nav.pw.repetir")}>
            <input
              type="password"
              value={repetir}
              onChange={(e) => setRepetir(e.target.value)}
              autoComplete="new-password"
            />
          </Campo>
          {repetir && nueva !== repetir && (
            <p className="pequeno" style={{ color: "var(--rojo)", margin: 0 }}>
              {t("nav.pw.noCoinciden")}
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
